/**
 * Magnetic declination from the World Magnetic Model 2025 (Sprint 103.1, ADR-0120 Rule 6).
 *
 * A phone's compass points to magnetic north; the sun's directions are from true
 * north. The difference, the declination, is about +1.5° in Paris, +3° in
 * Marseille and -15° in New York, so the path would sit that far off without it.
 * WMM2025 (NOAA and the British Geological Survey, public domain) gives it for
 * any place from 2025 to 2030, computed here with no network.
 *
 * The coefficients are the model's `WMM.COF`, degree and order 12: the main
 * field in nT at the 2025.0 epoch and its yearly change in nT a year.
 */

const EPOCH = 2025.0;
/** WGS84's semi-major axis and flattening, and the model's reference radius, in km. */
const A = 6378.137;
const F = 1 / 298.257223563;
const E2 = F * (2 - F);
const R0 = 6371.2;
const RADIANS = Math.PI / 180;
const MAX_DEGREE = 12;

/** `[n, m, g, h, ġ, ḣ]` from WMM.COF. */
const COEFFICIENTS: readonly (readonly [number, number, number, number, number, number])[] = [
  [1, 0, -29351.8, 0, 12, 0],
  [1, 1, -1410.8, 4545.4, 9.7, -21.5],
  [2, 0, -2556.6, 0, -11.6, 0],
  [2, 1, 2951.1, -3133.6, -5.2, -27.7],
  [2, 2, 1649.3, -815.1, -8, -12.1],
  [3, 0, 1361, 0, -1.3, 0],
  [3, 1, -2404.1, -56.6, -4.2, 4],
  [3, 2, 1243.8, 237.5, 0.4, -0.3],
  [3, 3, 453.6, -549.5, -15.6, -4.1],
  [4, 0, 895, 0, -1.6, 0],
  [4, 1, 799.5, 278.6, -2.4, -1.1],
  [4, 2, 55.7, -133.9, -6, 4.1],
  [4, 3, -281.1, 212, 5.6, 1.6],
  [4, 4, 12.1, -375.6, -7, -4.4],
  [5, 0, -233.2, 0, 0.6, 0],
  [5, 1, 368.9, 45.4, 1.4, -0.5],
  [5, 2, 187.2, 220.2, 0, 2.2],
  [5, 3, -138.7, -122.9, 0.6, 0.4],
  [5, 4, -142, 43, 2.2, 1.7],
  [5, 5, 20.9, 106.1, 0.9, 1.9],
  [6, 0, 64.4, 0, -0.2, 0],
  [6, 1, 63.8, -18.4, -0.4, 0.3],
  [6, 2, 76.9, 16.8, 0.9, -1.6],
  [6, 3, -115.7, 48.8, 1.2, -0.4],
  [6, 4, -40.9, -59.8, -0.9, 0.9],
  [6, 5, 14.9, 10.9, 0.3, 0.7],
  [6, 6, -60.7, 72.7, 0.9, 0.9],
  [7, 0, 79.5, 0, 0, 0],
  [7, 1, -77, -48.9, -0.1, 0.6],
  [7, 2, -8.8, -14.4, -0.1, 0.5],
  [7, 3, 59.3, -1, 0.5, -0.8],
  [7, 4, 15.8, 23.4, -0.1, 0],
  [7, 5, 2.5, -7.4, -0.8, -1],
  [7, 6, -11.1, -25.1, -0.8, 0.6],
  [7, 7, 14.2, -2.3, 0.8, -0.2],
  [8, 0, 23.2, 0, -0.1, 0],
  [8, 1, 10.8, 7.1, 0.2, -0.2],
  [8, 2, -17.5, -12.6, 0, 0.5],
  [8, 3, 2, 11.4, 0.5, -0.4],
  [8, 4, -21.7, -9.7, -0.1, 0.4],
  [8, 5, 16.9, 12.7, 0.3, -0.5],
  [8, 6, 15, 0.7, 0.2, -0.6],
  [8, 7, -16.8, -5.2, 0, 0.3],
  [8, 8, 0.9, 3.9, 0.2, 0.2],
  [9, 0, 4.6, 0, 0, 0],
  [9, 1, 7.8, -24.8, -0.1, -0.3],
  [9, 2, 3, 12.2, 0.1, 0.3],
  [9, 3, -0.2, 8.3, 0.3, -0.3],
  [9, 4, -2.5, -3.3, -0.3, 0.3],
  [9, 5, -13.1, -5.2, 0, 0.2],
  [9, 6, 2.4, 7.2, 0.3, -0.1],
  [9, 7, 8.6, -0.6, -0.1, -0.2],
  [9, 8, -8.7, 0.8, 0.1, 0.4],
  [9, 9, -12.9, 10, -0.1, 0.1],
  [10, 0, -1.3, 0, 0.1, 0],
  [10, 1, -6.4, 3.3, 0, 0],
  [10, 2, 0.2, 0, 0.1, 0],
  [10, 3, 2, 2.4, 0.1, -0.2],
  [10, 4, -1, 5.3, 0, 0.1],
  [10, 5, -0.6, -9.1, -0.3, -0.1],
  [10, 6, -0.9, 0.4, 0, 0.1],
  [10, 7, 1.5, -4.2, -0.1, 0],
  [10, 8, 0.9, -3.8, -0.1, -0.1],
  [10, 9, -2.7, 0.9, 0, 0.2],
  [10, 10, -3.9, -9.1, 0, 0],
  [11, 0, 2.9, 0, 0, 0],
  [11, 1, -1.5, 0, 0, 0],
  [11, 2, -2.5, 2.9, 0, 0.1],
  [11, 3, 2.4, -0.6, 0, 0],
  [11, 4, -0.6, 0.2, 0, 0.1],
  [11, 5, -0.1, 0.5, -0.1, 0],
  [11, 6, -0.6, -0.3, 0, 0],
  [11, 7, -0.1, -1.2, 0, 0.1],
  [11, 8, 1.1, -1.7, -0.1, 0],
  [11, 9, -1, -2.9, -0.1, 0],
  [11, 10, -0.2, -1.8, -0.1, 0],
  [11, 11, 2.6, -2.3, -0.1, 0],
  [12, 0, -2, 0, 0, 0],
  [12, 1, -0.2, -1.3, 0, 0],
  [12, 2, 0.3, 0.7, 0, 0],
  [12, 3, 1.2, 1, 0, -0.1],
  [12, 4, -1.3, -1.4, 0, 0.1],
  [12, 5, 0.6, 0, 0, 0],
  [12, 6, 0.6, 0.6, 0.1, 0],
  [12, 7, 0.5, -0.1, 0, 0],
  [12, 8, -0.1, 0.8, 0, 0],
  [12, 9, -0.4, 0.1, 0, 0],
  [12, 10, -0.2, -1, -0.1, 0],
  [12, 11, -1.3, 0.1, 0, 0],
  [12, 12, -0.7, 0.2, -0.1, -0.1]
];

/** The model's first and last valid decimal years. */
export const WMM_VALID_FROM = 2025.0;
export const WMM_VALID_TO = 2030.0;

/** The decimal year of an instant (ms since the epoch), as the model counts time. */
export function decimalYear(instant: number): number {
  const date = new Date(instant);
  const year = date.getUTCFullYear();
  const start = Date.UTC(year, 0, 1);
  const end = Date.UTC(year + 1, 0, 1);
  return year + (instant - start) / (end - start);
}

/**
 * The declination in degrees, east positive: true north is this many degrees
 * clockwise from magnetic north. Outside 2025-2030 the model is extrapolated,
 * which drifts by a few tenths of a degree a year: still better than nothing.
 */
export function declinationDegrees(
  latitudeDegrees: number,
  longitudeDegrees: number,
  year: number,
  heightKm = 0
): number {
  const dt = year - EPOCH;
  // Geodetic to geocentric spherical coordinates.
  const phi = Math.max(-89.999, Math.min(89.999, latitudeDegrees)) * RADIANS;
  const lambda = longitudeDegrees * RADIANS;
  const sinPhi = Math.sin(phi);
  const rc = A / Math.sqrt(1 - E2 * sinPhi * sinPhi);
  const p = (rc + heightKm) * Math.cos(phi);
  const z = (rc * (1 - E2) + heightKm) * sinPhi;
  const r = Math.hypot(p, z);
  const phiC = Math.asin(z / r);
  const cosT = Math.sin(phiC);
  const sinT = Math.cos(phiC);

  // Schmidt semi-normalised Legendre functions of the colatitude and their derivatives.
  const size = MAX_DEGREE + 1;
  const P = Array.from({ length: size }, () => new Array<number>(size).fill(0));
  const dP = Array.from({ length: size }, () => new Array<number>(size).fill(0));
  P[0]![0] = 1;
  for (let n = 1; n <= MAX_DEGREE; n += 1) {
    for (let m = 0; m <= n; m += 1) {
      if (n === m) {
        P[n]![m] = sinT * P[n - 1]![m - 1]!;
        dP[n]![m] = sinT * dP[n - 1]![m - 1]! + cosT * P[n - 1]![m - 1]!;
      } else if (n === 1) {
        P[n]![m] = cosT * P[0]![0]!;
        dP[n]![m] = -sinT * P[0]![0]!;
      } else {
        const k = ((n - 1) * (n - 1) - m * m) / ((2 * n - 1) * (2 * n - 3));
        P[n]![m] = cosT * P[n - 1]![m]! - (m > n - 2 ? 0 : k * P[n - 2]![m]!);
        dP[n]![m] =
          cosT * dP[n - 1]![m]! - sinT * P[n - 1]![m]! - (m > n - 2 ? 0 : k * dP[n - 2]![m]!);
      }
    }
  }
  // From Gauss to Schmidt normalisation.
  const S = Array.from({ length: size }, () => new Array<number>(size).fill(0));
  S[0]![0] = 1;
  for (let n = 1; n <= MAX_DEGREE; n += 1) {
    S[n]![0] = (S[n - 1]![0]! * (2 * n - 1)) / n;
    for (let m = 1; m <= n; m += 1) {
      S[n]![m] = S[n]![m - 1]! * Math.sqrt(((n - m + 1) * (m === 1 ? 2 : 1)) / (n + m));
    }
  }

  let north = 0;
  let east = 0;
  let down = 0;
  for (const [n, m, g0, h0, gDot, hDot] of COEFFICIENTS) {
    const g = (g0 + gDot * dt) * S[n]![m]!;
    const h = (h0 + hDot * dt) * S[n]![m]!;
    const ratio = Math.pow(R0 / r, n + 2);
    const cosM = Math.cos(m * lambda);
    const sinM = Math.sin(m * lambda);
    const along = g * cosM + h * sinM;
    north += ratio * along * dP[n]![m]!;
    east += (ratio * m * (g * sinM - h * cosM) * P[n]![m]!) / sinT;
    down -= (n + 1) * ratio * along * P[n]![m]!;
  }
  // The north component is geocentric; turned back to the ellipsoid's.
  const psi = phiC - phi;
  const northGeodetic = north * Math.cos(psi) - down * Math.sin(psi);
  return Math.atan2(east, northGeodetic) / RADIANS;
}
